/**
 * ResultsSection - Results section for scroll wizard
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTournament } from '../../contexts/TournamentContext';
import MatchScore from '../../models/MatchScore';
import useFirebaseSave from '../../hooks/useFirebaseSave';

const ResultsSection = () => {
  const {
    schedule,
    currentRound,
    config,
    courts,
    getPlayerName,
    setCurrentRound,
    getCurrentRound,
    recalculateRanking,
    setSchedule,
    tournamentStarted,
    setTournamentStarted
  } = useTournament();

  const [, setRefresh] = useState(0);
  const { saveNow } = useFirebaseSave();
  const validatedCountRef = useRef(null);

  // Force re-render when round changes
  useEffect(() => {
    setRefresh(prev => prev + 1);
  }, [currentRound]);

  // Immediate save when a match is validated or unvalidated
  useEffect(() => {
    if (!schedule?.rounds) return;
    const total = schedule.rounds.reduce(
      (acc, r) => acc + r.matches.filter(m => m.validated === true).length,
      0
    );
    if (validatedCountRef.current !== null && total !== validatedCountRef.current) {
      saveNow();
    }
    validatedCountRef.current = total;
  }, [schedule, saveNow]);

  if (!schedule || !schedule.rounds || schedule.rounds.length === 0) {
    return (
      <div className="section-inner">
        <div className="section-header">
          <h1>
            <i className="bi bi-clipboard-check me-3"></i>
            Résultats
          </h1>
        </div>
        <div className="section-body">
          <div className="alert alert-warning">
            Aucun planning n'a encore été généré.
          </div>
        </div>
      </div>
    );
  }

  const round = getCurrentRound();
  if (!round) {
    return (
      <div className="section-inner">
        <div className="section-header">
          <h1>
            <i className="bi bi-clipboard-check me-3"></i>
            Résultats
          </h1>
        </div>
        <div className="section-body">
          <div className="alert alert-danger">Tour introuvable.</div>
        </div>
      </div>
    );
  }

  // Ensure all matches have detailedScore initialized
  round.matches.forEach(match => {
    if (!match.detailedScore) {
      match.detailedScore = new MatchScore(
        config.setsToWin || 1,
        config.gamesPerSet !== undefined ? config.gamesPerSet : null,
        config.tiebreakPoints || 7,
        config.allowDraws !== undefined ? config.allowDraws : true
      );
    }
  });

  // Handle score input change
  const handleScoreInput = (matchIndex, setIndex, team, value, isTiebreak = false) => {
    const updatedSchedule = { ...schedule };
    const match = updatedSchedule.rounds[currentRound].matches[matchIndex];

    const parsedValue = parseInt(value) || 0;

    // Update games
    const team1Input = team === 1 ? parsedValue : (match.detailedScore.sets[setIndex]?.team1 || 0);
    const team2Input = team === 2 ? parsedValue : (match.detailedScore.sets[setIndex]?.team2 || 0);
    match.detailedScore.setSetScore(setIndex, team1Input, team2Input, null);

    // Recalculate match result
    match.detailedScore.calculate();

    // Update match.result for compatibility
    if (match.detailedScore.isComplete()) {
      if (match.detailedScore.isDraw()) {
        match.result = 0;
      } else {
        match.result = match.detailedScore.winner === 0 ? 1 : 2;
      }
    } else {
      match.result = null;
    }

    setSchedule(updatedSchedule);
  };

  // Validate a single match
  const validateMatch = (matchIndex) => {
    const match = round.matches[matchIndex];
    if (!match.detailedScore || !match.detailedScore.isComplete()) {
      return;
    }

    // Build individual player results from team stats
    const team1Stats = match.detailedScore.stats.team1;
    const team2Stats = match.detailedScore.stats.team2;
    const playerResults = {};
    match.team1.forEach(playerId => {
      playerResults[playerId] = {
        gamesWon: team1Stats.gamesWon,
        gamesLost: team1Stats.gamesLost,
        setsWon: team1Stats.setsWon,
        setsLost: team1Stats.setsLost
      };
    });
    match.team2.forEach(playerId => {
      playerResults[playerId] = {
        gamesWon: team2Stats.gamesWon,
        gamesLost: team2Stats.gamesLost,
        setsWon: team2Stats.setsWon,
        setsLost: team2Stats.setsLost
      };
    });

    // Deep copy to ensure React detects changes
    const updatedSchedule = {
      ...schedule,
      rounds: schedule.rounds.map((r, idx) => {
        if (idx === currentRound) {
          return {
            ...r,
            matches: r.matches.map((m, mIdx) => {
              if (mIdx === matchIndex) {
                return { ...m, validated: true, playerResults };
              }
              return m;
            })
          };
        }
        return r;
      })
    };

    setSchedule(updatedSchedule);

    // Recalculate ranking with the updated schedule to avoid stale closure
    recalculateRanking(updatedSchedule);

    // Force refresh
    setRefresh(prev => prev + 1);
  };

  // Unvalidate a match for editing
  const unvalidateMatch = (matchIndex) => {
    // Deep copy to ensure React detects changes
    const updatedSchedule = {
      ...schedule,
      rounds: schedule.rounds.map((r, idx) => {
        if (idx === currentRound) {
          return {
            ...r,
            validated: false,
            matches: r.matches.map((m, mIdx) => {
              if (mIdx === matchIndex) {
                const { playerResults, ...matchWithoutResults } = m;
                return { ...matchWithoutResults, validated: false };
              }
              return m;
            })
          };
        }
        return r;
      })
    };

    setSchedule(updatedSchedule);

    // Recalculate ranking with the updated schedule to avoid stale closure
    recalculateRanking(updatedSchedule);

    // Force refresh
    setRefresh(prev => prev + 1);
  };

  // Navigate rounds
  const goToPreviousRound = () => {
    if (currentRound > 0) {
      setCurrentRound(currentRound - 1);
    }
  };

  const goToNextRound = () => {
    if (currentRound < schedule.rounds.length - 1) {
      const allValidated = round.matches.every(m => m.validated === true);
      if (!allValidated) {
        alert('⚠️ Veuillez valider tous les terrains avant de passer au tour suivant.');
        return;
      }
      // Mark tournament as started when leaving the first round
      if (currentRound === 0 && !tournamentStarted) {
        setTournamentStarted(true);
      }
      setCurrentRound(currentRound + 1);
    }
  };

  const validatedCount = round.matches.filter(m => m.validated === true).length;
  const totalMatches = round.matches.length;
  const allValidated = validatedCount === totalMatches;
  const isLastRound = currentRound === schedule.rounds.length - 1;

  return (
    <div className="section-inner">
      <div className="section-header">
        <h1>
          <i className="bi bi-clipboard-check me-3"></i>
          Résultats
        </h1>
      </div>

      <div className="section-body">
        {/* Round Info */}
        <div className="d-flex justify-content-center gap-3 mb-3">
          <span className="badge bg-warning text-dark fs-6">
            Tour {currentRound + 1} / {schedule.rounds.length}
          </span>
        </div>

        {/* Validation Progress */}
        <div className={`alert ${allValidated ? 'alert-success text-white' : validatedCount > 0 ? 'alert-warning text-white' : 'alert-secondary'} mb-4`}>
          <i className="bi bi-clipboard-check me-2"></i>
          <strong>Progression :</strong> {validatedCount}/{totalMatches} terrains validés
        </div>

        {/* Matches */}
        <div className="matches-results mb-4">
          {round.matches.map((match, matchIndex) => (
            <MatchScoreCard
              key={matchIndex}
              match={match}
              matchIndex={matchIndex}
              config={config}
              courts={courts}
              getPlayerName={getPlayerName}
              onScoreChange={handleScoreInput}
              onValidate={validateMatch}
              onUnvalidate={unvalidateMatch}
            />
          ))}
        </div>

        {/* Resting Players */}
        {round.restingPlayers && round.restingPlayers.length > 0 && (
          <div className="p-3 rounded mb-4" style={{ background: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '1rem' }}>
            <h6 className="text-muted mb-2">
              <i className="bi bi-pause-circle me-2"></i>
              Joueurs au repos
            </h6>
            <div className="d-flex flex-wrap gap-2">
              {round.restingPlayers.map(playerId => (
                <span key={playerId} className="badge bg-secondary">
                  <i className="bi bi-person me-1"></i>
                  {getPlayerName(playerId)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Round Navigation */}
        <div className="d-flex flex-wrap gap-2 justify-content-between">
          {currentRound > 0 && (
            <button
              type="button"
              className="btn btn-outline-light"
              onClick={goToPreviousRound}
            >
              <i className="bi bi-chevron-left me-2"></i>
              <span className="btn-text-desktop">Tour précédent</span>
            </button>
          )}

          {!isLastRound ? (
            <button
              type="button"
              className="btn btn-outline-light ms-auto"
              onClick={goToNextRound}
              disabled={!allValidated}
              title={!allValidated ? 'Validez tous les terrains avant de continuer' : 'Aller au tour suivant'}
            >
              <span className="btn-text-desktop">Tour suivant</span>
              <i className="bi bi-chevron-right ms-2"></i>
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-success btn-lg"
              onClick={() => {
                const rankingSection = document.getElementById('section-ranking');
                if (rankingSection) {
                  rankingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              disabled={!allValidated}
              title={!allValidated ? 'Validez tous les terrains avant de voir le classement' : 'Voir le classement final'}
            >
              <i className="bi bi-trophy-fill me-2"></i>
              Classement
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Match Score Card Component
const MatchScoreCard = ({ match, matchIndex, config, courts, getPlayerName, onScoreChange, onValidate, onUnvalidate }) => {
  const courtName = courts?.[match.courtIndex]?.name || `Terrain ${match.courtIndex + 1}`;
  const [isExpanded, setIsExpanded] = React.useState(!match.validated);
  const isValidated = match.validated === true;

  React.useEffect(() => {
    setIsExpanded(!match.validated);
  }, [match.validated]);
  const isComplete = match.detailedScore.isComplete();
  const maxSets = Math.min(config.setsToWin * 2 - 1, 3);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const team1Names = match.team1.map(id => getPlayerName(id)).join(' & ');
  const team2Names = match.team2.map(id => getPlayerName(id)).join(' & ');
  const playedSets = match.detailedScore.sets.filter(s => s && (s.team1 > 0 || s.team2 > 0));
  const team1Scores = playedSets.map(s => s.team1);
  const team2Scores = playedSets.map(s => s.team2);
  const isTeam1Winner = match.detailedScore.isComplete() && !match.detailedScore.isDraw() && match.detailedScore.winner === 0;
  const isTeam2Winner = match.detailedScore.isComplete() && !match.detailedScore.isDraw() && match.detailedScore.winner === 1;

  return (
    <div className={`match-score-card mb-3 p-3 ${isComplete ? 'score-complete' : ''} ${isValidated ? 'match-validated' : ''}`}>
      {/* Court Header */}
      <div
        className="d-flex justify-content-between align-items-center mb-3"
        onClick={toggleExpand}
        style={{ cursor: 'pointer' }}
      >
        {isValidated && !isExpanded ? (
          <div className="d-flex flex-column flex-grow-1 me-2 gap-1">
            <span className="text-white-50 small">
              <i className="bi bi-geo-alt me-1"></i>{courtName}
            </span>
            <div className="d-flex align-items-center justify-content-between gap-2">
              <span className={`small fw-bold ${isTeam1Winner ? 'text-white' : 'text-white-50'}`}>
                {isTeam1Winner && <i className="bi bi-trophy-fill me-1 text-warning"></i>}
                {team1Names}
              </span>
              <div className="d-flex gap-1">
                {team1Scores.map((s, i) => (
                  <span key={i} className={`badge ${isTeam1Winner ? 'bg-light text-dark' : 'bg-white bg-opacity-25 text-white'}`}>{s}</span>
                ))}
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between gap-2">
              <span className={`small fw-bold ${isTeam2Winner ? 'text-white' : 'text-white-50'}`}>
                {isTeam2Winner && <i className="bi bi-trophy-fill me-1 text-warning"></i>}
                {team2Names}
              </span>
              <div className="d-flex gap-1">
                {team2Scores.map((s, i) => (
                  <span key={i} className={`badge ${isTeam2Winner ? 'bg-light text-dark' : 'bg-white bg-opacity-25 text-white'}`}>{s}</span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <span className="fw-bold text-white">
            <i className="bi bi-geo-alt me-1"></i>
            {courtName}
            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} ms-2`}></i>
          </span>
        )}
        <div onClick={(e) => e.stopPropagation()}>
          {isValidated ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={() => { onUnvalidate(matchIndex); setIsExpanded(true); }}
              title="Modifier"
            >
              <i className="bi bi-pencil-fill"></i>
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={() => { onValidate(matchIndex); setIsExpanded(false); }}
              disabled={!isComplete}
              title={isComplete ? 'Valider ce terrain' : 'Complétez le score pour valider'}
            >
              <i className="bi bi-check-circle me-1"></i>Valider
            </button>
          )}
        </div>
      </div>

      {/* Score Table */}
      {isExpanded && (
        <table className="table table-sm table-bordered mb-3">
        <thead>
          <tr>
            <th>Paires</th>
            {[...Array(maxSets)].map((_, i) => (
              <th key={i} className="text-center">SET {i + 1}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Team 1 */}
          <TeamScoreRow
            team={1}
            teamPlayers={match.team1}
            match={match}
            matchIndex={matchIndex}
            maxSets={maxSets}
            config={config}
            getPlayerName={getPlayerName}
            isValidated={isValidated}
            onScoreChange={onScoreChange}
          />
          {/* Team 2 */}
          <TeamScoreRow
            team={2}
            teamPlayers={match.team2}
            match={match}
            matchIndex={matchIndex}
            maxSets={maxSets}
            config={config}
            getPlayerName={getPlayerName}
            isValidated={isValidated}
            onScoreChange={onScoreChange}
          />
        </tbody>
      </table>
      )}
    </div>
  );
};

// Team Score Row Component
const TeamScoreRow = ({ team, teamPlayers, match, matchIndex, maxSets, config, getPlayerName, isValidated, onScoreChange }) => {
  const playerNames = teamPlayers.map(id => getPlayerName(id)).join(' & ');
  const isWinner = match.detailedScore.isComplete() && match.detailedScore.winner === (team === 1 ? 0 : 1);
  const isLoser = match.detailedScore.isComplete() && !match.detailedScore.isDraw() && !isWinner;

  return (
    <tr className={isWinner ? 'table-success' : isLoser ? 'table-danger' : ''}>
      <td>
        <div className="fw-bold">{playerNames}</div>
      </td>
      {[...Array(maxSets)].map((_, setIndex) => (
        <SetInputCell
          key={setIndex}
          match={match}
          matchIndex={matchIndex}
          setIndex={setIndex}
          team={team}
          config={config}
          isValidated={isValidated}
          onScoreChange={onScoreChange}
        />
      ))}
    </tr>
  );
};

// Set Input Cell Component
const SetInputCell = ({ match, matchIndex, setIndex, team, config, isValidated, onScoreChange }) => {
  const set = match.detailedScore.sets[setIndex];
  const gamesValue = set ? (team === 1 ? set.team1 : set.team2) : '';

  const setWinner = set && set.team1 !== set.team2 ? (set.team1 > set.team2 ? 1 : 2) : null;
  const isWinnerCell = setWinner === team;
  const isLoserCell = setWinner !== null && setWinner !== team;

  return (
    <td className="text-center">
      <input
        type="number"
        className={`form-control form-control-sm text-center ${isWinnerCell ? 'border-success' : isLoserCell ? 'border-danger' : ''}`}
        style={{ width: '60px' }}
        min="0"
        max="20"
        value={gamesValue}
        placeholder="-"
        readOnly={isValidated}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onScoreChange(matchIndex, setIndex, team, e.target.value)}
      />
    </td>
  );
};

export default ResultsSection;
